import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import './SignupSurvey.css';
import { ANALYTICS_EVENTS, identifyUserFromLeadCapture, pushEvent } from '../../utils/analytics';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import { completeStripeAutoLogin } from '../../utils/stripeAutoLogin';
import {
  buildSmsConsentPayload,
  shouldShowSmsConsent,
  SmsConsentCheckbox,
} from '../SmsConsent';

// Focused options for better UX.
const DISCOVERY_OPTIONS = [
  'Search Engines (Google, Bing)',
  'YouTube', 
  'Social Media (Instagram, TikTok, Twitter)',
  'Word of Mouth/Friend Recommendation',
  'Affiliate Link',
  'Paid Advertisement',
  'Other'
];

// 🎯 IMPROVED: Feature-focused options for better business intelligence
const GOAL_OPTIONS = [
  'Backtest trading strategies to validate performance',
  'Deploy live trading bots with real money',
  'Browse and use community-created strategies',
  'Use Lumivest\'s proven strategies with track records',
  'Learn algorithmic trading step-by-step',
  'Build custom trading strategies from scratch',
  'Other'
];

const SignupSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, getApiToken, handleCustomJwtAuth } = useUnifiedAuth();
  const isForcedAttribution = searchParams.get('forced_attribution') === 'true';
  
  // Get email from URL params or navigation state
  const email = searchParams.get('email') || location.state?.email;
  const phoneNumber =
    searchParams.get('phone') ||
    searchParams.get('phoneNumber') ||
    location.state?.phone ||
    location.state?.phoneNumber ||
    '';
  
  // Extract all query parameters to forward to upgrade-to-pro page
  const allQueryParams = {};
  for (const [key, value] of searchParams.entries()) {
    allQueryParams[key] = value;
  }
  
  // Randomize the order of options on component mount
  const [discoveryOptions] = useState(() => [...DISCOVERY_OPTIONS].sort(() => Math.random() - 0.5));
  const [goalOptions] = useState(() => [...GOAL_OPTIONS].sort(() => Math.random() - 0.5));
  
  // Question flow state
  const [currentQuestion, setCurrentQuestion] = useState(1); // 1 or 2
  const totalQuestions = isForcedAttribution ? 1 : 2;
  
  const [formData, setFormData] = useState({
    discoverySource: '',
    discoveryDetails: '',
    userGoal: '',
    goalDetails: ''
  });
  
  const [showDetailsInput, setShowDetailsInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState('');

  const buildSurveyAnalyticsPayload = (extra = {}) => ({
    source: '/signup-survey',
    email: email || undefined,
    source_flow: searchParams.get('source_flow') || 'unknown',
    forced_attribution: isForcedAttribution,
    redirect_to: searchParams.get('redirectTo') || undefined,
    has_email: Boolean(email),
    ...extra,
  });

  useEffect(() => {
    pushEvent({
      event: ANALYTICS_EVENTS.SIGNUP_SURVEY_STARTED,
      ...buildSurveyAnalyticsPayload(),
    });
    // This should fire once for the loaded survey URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDiscoveryAnswer = (value) => {
    setFormData(prev => ({ ...prev, discoverySource: value, discoveryDetails: '' }));
    setShowDetailsInput(true);
  };

  const handleGoalAnswer = (value) => {
    setFormData(prev => ({ ...prev, userGoal: value, goalDetails: '' }));
    setShowDetailsInput(true);
  };

  const handleDetailsChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContinueToNextQuestion = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(2);
      setShowDetailsInput(false);
    } else {
      // Submit the survey
      handleSubmit();
    }
  };

  // Helper function to determine next destination after survey completion
  const buildNextDestinationUrl = () => {
    const requestedRedirect = searchParams.get('redirectTo');
    if (requestedRedirect && requestedRedirect.startsWith('/') && !requestedRedirect.startsWith('//')) {
      const redirectUrl = new URL(requestedRedirect, window.location.origin);
      redirectUrl.searchParams.set('survey_completed', 'true');
      return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
    }

    // 🚨 SIMPLIFIED LOGIC: Survey ALWAYS goes to onboarding
    // The survey only appears after signup, so users should always proceed to onboarding
    
    const onboardingParams = new URLSearchParams();
    
    // Forward all relevant parameters for onboarding
    if (email) onboardingParams.append('email', email);
    if (searchParams.get('name')) onboardingParams.append('name', searchParams.get('name'));
    if (searchParams.get('customerId')) onboardingParams.append('customerId', searchParams.get('customerId'));
    if (searchParams.get('paymentMethodId')) onboardingParams.append('paymentMethodId', searchParams.get('paymentMethodId'));
    if (searchParams.get('last4')) onboardingParams.append('last4', searchParams.get('last4'));
    if (searchParams.get('from_starter_signup')) onboardingParams.append('from_starter_signup', searchParams.get('from_starter_signup'));
    
    // Add completion tracking
    onboardingParams.append('survey_completed', 'true');
    onboardingParams.append('source_flow', searchParams.get('source_flow') || 'signup_survey_completion');
    
    const queryString = onboardingParams.toString();
    const finalUrl = `/onboarding${queryString ? `?${queryString}` : ''}`;
    return finalUrl;
  };

  const getAutoLoginProof = () => {
    const paymentIntentId = searchParams.get('payment_intent') || searchParams.get('payment_intent_id');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
    const setupIntentId = searchParams.get('setup_intent') || searchParams.get('setup_intent_id');
    const setupIntentClientSecret = searchParams.get('setup_intent_client_secret');
    return {
      paymentIntentId,
      paymentIntentClientSecret,
      setupIntentId,
      setupIntentClientSecret,
      hasProof: Boolean(
        (paymentIntentId && paymentIntentClientSecret) ||
        (setupIntentId && setupIntentClientSecret)
      ),
    };
  };

  const shouldAttemptAutoLogin = () =>
    searchParams.get('auto_login') === 'true' && getAutoLoginProof().hasProof;

  // Persist survey (even partial) without blocking navigation
  const submitSurvey = async (reason = 'completed') => {
    try {
      const smsConsentPayload = shouldShowSmsConsent(phoneNumber)
        ? buildSmsConsentPayload({
            source: 'signup_survey_post_purchase',
            phoneNumber,
            accepted: smsConsentAccepted,
          })
        : null;

      if (email) {
        identifyUserFromLeadCapture(
          email,
          {
            ...formData,
            phone: phoneNumber || undefined,
            ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
            lead_type: 'signup_survey',
            form_step: reason,
            survey_source: searchParams.get('source_flow') || 'unknown',
          },
          'signup_survey_page',
        );
      }

      const token = isAuthenticated ? await getApiToken().catch(() => null) : null;
      const endpoint = token ? '/users/signup-survey' : '/users/signup-survey-anonymous';
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: email || null, // Include email for anonymous survey
          ...(phoneNumber ? { phoneNumber } : {}),
          ...formData,
          ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
          survey_status: reason,
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[SignupSurvey] Failed to save survey:', response.statusText, errorData);
      }
    } catch (error) {
      console.error('[SignupSurvey] Failed to save survey:', error);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    await submitSurvey('completed');

    pushEvent({
      event: ANALYTICS_EVENTS.SIGNUP_SURVEY_COMPLETED,
      ...buildSurveyAnalyticsPayload({
        survey_responses: {
          discoverySource: formData.discoverySource,
          discoveryDetails: formData.discoveryDetails,
          userGoal: formData.userGoal,
          goalDetails: formData.goalDetails,
        },
        sms_consent_accepted: Boolean(smsConsentAccepted),
        sms_consent_present: shouldShowSmsConsent(phoneNumber),
      }),
    });
    
    try {
      // Check if auto-login is requested after survey completion
      if (shouldAttemptAutoLogin()) {
        // Trigger auto-login after survey completion
        await attemptAutoLoginAfterSurvey();
      } else {
        // Navigate to next destination (onboarding for Pro purchases, upgrade-to-pro for Starter)
        navigate(buildNextDestinationUrl());
      }
      
    } catch (error) {
      console.error('[SignupSurvey] Failed to save survey:', error);
      // Navigate anyway - don't block the user flow
      navigate(buildNextDestinationUrl());
    } finally {
      setIsSubmitting(false);
    }
  };

  const attemptAutoLoginAfterSurvey = async () => {
    try {
      const customerId = searchParams.get('customerId');
      const autoLoginProof = getAutoLoginProof();
      
      // Call the auto-login endpoint
      const autoLoginBody = {};
      if (email) autoLoginBody.email = email;
      if (customerId) autoLoginBody.customer_id = customerId;
      if (autoLoginProof.paymentIntentId) autoLoginBody.payment_intent_id = autoLoginProof.paymentIntentId;
      if (autoLoginProof.paymentIntentClientSecret) autoLoginBody.payment_intent_client_secret = autoLoginProof.paymentIntentClientSecret;
      if (autoLoginProof.setupIntentId) autoLoginBody.setup_intent_id = autoLoginProof.setupIntentId;
      if (autoLoginProof.setupIntentClientSecret) autoLoginBody.setup_intent_client_secret = autoLoginProof.setupIntentClientSecret;
      
      // Construct the redirect URL - should go to the intended destination (onboarding), not current page
      const nextDestination = buildNextDestinationUrl();
      const nextUrl = new URL(nextDestination, window.location.origin);
      
      // Remove any payment-related params that we don't want to keep after login
      nextUrl.searchParams.delete('payment_intent');
      nextUrl.searchParams.delete('payment_intent_client_secret');
      nextUrl.searchParams.delete('redirect_status');
      nextUrl.searchParams.delete('setup_intent');
      nextUrl.searchParams.delete('setup_intent_client_secret');
      
      // Use the next destination as redirectTo (not current signup-survey page)
      const redirectTo = nextUrl.pathname + nextUrl.search;
      autoLoginBody.redirectTo = redirectTo;
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/stripe/autoLoginAfterPurchase`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(autoLoginBody)
        }
      );
      
      if (!response.ok) {
        throw new Error(`Auto-login request failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        if (await completeStripeAutoLogin(data, {
          handleCustomJwtAuth,
          fallbackUrl: redirectTo,
          navigate,
        })) {
          return;
        } else if (data.method === 'set_password' && data.setPasswordUrl) {
          // Set password URL generated - redirect user to set their password
          window.location.href = data.setPasswordUrl;
          return;
          
        } else if (data.method === 'email_instructions') {
          // User should check email for instructions
          alert('Account created! Please check your email for login instructions.');
          // Continue with normal survey flow
          return;
        }
      }
      
      // Fallback: if auto-login fails, continue to next destination
      navigate(buildNextDestinationUrl());
      
    } catch (error) {
      console.error('[SignupSurvey] Auto-login error:', error);
      // Fallback: if auto-login fails, continue to next destination
      navigate(buildNextDestinationUrl());
    }
  };

  const handleSkip = () => {
    if (currentQuestion === 1) {
      setCurrentQuestion(2);
      setShowDetailsInput(false);
    } else {
      // On the last question, check for auto-login like handleSkipAll
      submitSurvey('skipped_question'); // fire-and-forget partial save
      
      if (shouldAttemptAutoLogin()) {
        // Trigger auto-login after skipping final question
        attemptAutoLoginAfterSurvey();
      } else {
        navigate(buildNextDestinationUrl());
      }
    }
  };

  const handleSkipAll = () => {
    // Check if auto-login is requested after survey completion (even when skipped)
    submitSurvey('skipped_all'); // fire-and-forget partial save
    
    if (shouldAttemptAutoLogin()) {
      // Trigger auto-login after skipping survey
      attemptAutoLoginAfterSurvey();
    } else {
      navigate(buildNextDestinationUrl());
    }
  };

  // Get current question data
  const getCurrentQuestionData = () => {
    if (currentQuestion === 1) {
      return {
        title: 'How did you find us?',
        options: discoveryOptions,
        selectedValue: formData.discoverySource,
        onAnswer: handleDiscoveryAnswer,
        detailsField: 'discoveryDetails',
        detailsValue: formData.discoveryDetails,
        detailsPlaceholder: 'e.g., specific YouTube channel, website URL, person who referred you...'
      };
    } else {
      return {
        title: 'What are you hoping to accomplish?',
        options: goalOptions,
        selectedValue: formData.userGoal,
        onAnswer: handleGoalAnswer,
        detailsField: 'goalDetails',
        detailsValue: formData.goalDetails,
        detailsPlaceholder: 'e.g., specific strategies you want to test, income goals, learning objectives...'
      };
    }
  };

  const questionData = getCurrentQuestionData();
  const showEmailWarning = !email;
  const progressPercentage = (currentQuestion / totalQuestions) * 100;
  const showSmsConsent = currentQuestion === 1 && shouldShowSmsConsent(phoneNumber);

  return (
    <div className="lumivest-signup-survey">
      <div className="lumivest-survey-container">
        <div className="lumivest-survey-header">
          <h2>{isForcedAttribution ? 'One quick question' : 'Help Us Serve You Better'}</h2>
          <p>
            {isForcedAttribution
              ? 'Please tell us how you found BotSpot before continuing.'
              : 'Your answers help us improve our platform and provide you with the best experience.'}
          </p>
          {showEmailWarning && (
            <div className="lumivest-email-warning">
              Note: Survey will be saved anonymously (email not detected in flow)
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="lumivest-progress-bar">
          <div 
            className="lumivest-progress-fill" 
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="lumivest-survey-content">
          <div className="lumivest-question-section">
            <div className="lumivest-question-counter">
              Question {currentQuestion} of {totalQuestions}
            </div>
            
            <h3>{questionData.title}</h3>
            
            <div className="lumivest-options-grid">
              {questionData.options.map(option => (
                <label key={option} className={`lumivest-option-card ${questionData.selectedValue === option ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={`question${currentQuestion}`}
                    value={option}
                    checked={questionData.selectedValue === option}
                    onChange={(e) => questionData.onAnswer(e.target.value)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            
            {showDetailsInput && questionData.selectedValue && (
              <div className="lumivest-details-section">
                <label htmlFor={`details${currentQuestion}`}>
                  Tell us more (optional)
                </label>
                <textarea
                  id={`details${currentQuestion}`}
                  placeholder={questionData.detailsPlaceholder}
                  value={questionData.detailsValue}
                  onChange={(e) => handleDetailsChange(questionData.detailsField, e.target.value)}
                />
              </div>
            )}

            {showSmsConsent && (
              <div className="lumivest-survey-sms-consent">
                <SmsConsentCheckbox
                  id="signup-survey-sms-consent"
                  checked={smsConsentAccepted}
                  onChange={(checked) => {
                    setSmsConsentAccepted(checked);
                    if (checked) setSmsConsentError('');
                  }}
                  error={smsConsentError}
                  tone="dark"
                  compact
                />
              </div>
            )}
          </div>
        </div>

        <div className="lumivest-survey-actions">
          {!isForcedAttribution && (
            <button 
              type="button" 
              className="lumivest-survey-btn lumivest-survey-btn-secondary"
              onClick={currentQuestion === totalQuestions ? handleSkipAll : handleSkip}
              disabled={isSubmitting}
            >
              {currentQuestion === totalQuestions ? 'Skip Survey' : 'Skip Question'}
            </button>
          )}
          
          {questionData.selectedValue && (
            <button 
              type="button" 
              className={`lumivest-survey-btn lumivest-survey-btn-primary ${currentQuestion === totalQuestions ? 'sparkle-button' : ''}`}
              onClick={handleContinueToNextQuestion}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : (currentQuestion === totalQuestions
                  ? (isForcedAttribution ? 'Continue' : 'Complete Survey')
                  : 'Next Question')}
              {!isSubmitting && currentQuestion === totalQuestions && (
                <>
                  <span className="sparkle"></span>
                  <span className="sparkle"></span>
                  <span className="sparkle"></span>
                  <span className="sparkle"></span>
                  <span className="sparkle"></span>
                  <span className="sparkle"></span>
                  <span className="sparkle"></span>
                  <span className="sparkle"></span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupSurvey; 
